import { KARAOKE_TRACK_NUMBER } from '@src/editor/constants';

import { deepClone } from './deepClone';

import type { ElementState } from '@src/renderer/ElementState';
import type { KaraokeConfig } from '@src/services/karaoke/karaokeTypes';
import type {
  TranscriptChange,
  TranscriptElement,
  TranscriptTextElement,
} from '@src/types.ts/transcript';

export function getClosestNotRemovedTextIndexToRight(
  startIdx: number,
  elements: TranscriptElement[],
  includeMuted: boolean = true,
) {
  let textIndexToRight = -1;
  while (startIdx < elements.length) {
    // console.log('check for text', startIdx);
    if (
      elements[startIdx].state !== 'removed' &&
      elements[startIdx].state !== 'cut' &&
      elements[startIdx].type === 'text' &&
      (includeMuted || elements[startIdx].state !== 'muted')
    ) {
      textIndexToRight = startIdx;
      break;
    }
    startIdx++;
  }

  return textIndexToRight;
}

export function getTimeBufferBefore(
  position: number,
  originalElements: TranscriptElement[],
) {
  if (originalElements[position].type !== 'text') {
    throw Error('Not a text element');
  }
  return (
    (originalElements[position] as TranscriptTextElement).buffer_before_ts || 0
  );
}

export function getTimeBufferAfter(
  position: number,
  originalElements: TranscriptElement[],
) {
  if (originalElements[position].type !== 'text') {
    throw Error('Not a text element');
  }
  return (
    (originalElements[position] as TranscriptTextElement).buffer_after_ts || 0
  );
}

export function getClosestNotRemovedElementIndexToLeft(
  startIdx: number,
  elements: TranscriptElement[],
  includeMuted: boolean = true,
  includeEmpty: boolean = false,
) {
  let textIndexToLeft = -1;
  while (startIdx >= 0) {
    if (
      elements[startIdx].state !== 'removed' &&
      elements[startIdx].state !== 'cut' &&
      (includeEmpty || elements[startIdx].value) &&
      (includeMuted || elements[startIdx].state !== 'muted')
    ) {
      textIndexToLeft = startIdx;
      break;
    }
    startIdx--;
  }

  return textIndexToLeft;
}

export function getClosestNotRemovedElementIndexToRight(
  startIdx: number,
  elements: TranscriptElement[],
  includeMuted: boolean = true,
  includeEmpty: boolean = false,
) {
  let textIndexToLeft = -1;
  while (startIdx < elements.length) {
    if (
      elements[startIdx].state !== 'removed' &&
      elements[startIdx].state !== 'cut' &&
      (includeEmpty || elements[startIdx].value) &&
      (includeMuted || elements[startIdx].state !== 'muted')
    ) {
      textIndexToLeft = startIdx;
      break;
    }
    startIdx++;
  }

  return textIndexToLeft;
}

export function getClosestElementIndexToLeftByFilter(
  startIdx: number,
  elements: TranscriptElement[],
  filter: (el: TranscriptElement, index: number) => boolean,
) {
  let textIndexToLeft = -1;
  // Clamp start to the array's last valid index — callers can pass
  // startIdx >= elements.length when a stale cursor lives past a
  // shrunken transcript; without the clamp the filter is invoked on
  // `undefined` and crashes.
  if (startIdx >= elements.length) startIdx = elements.length - 1;
  while (startIdx >= 0) {
    const el = elements[startIdx];
    if (el !== undefined && filter(el, startIdx)) {
      textIndexToLeft = startIdx;
      break;
    }
    startIdx--;
  }

  return textIndexToLeft;
}

/**
 * Canonical answer to "which real words does a transcript selection cover?".
 * The raw DOM selection can land its anchor/focus on whitespace or a removed
 * word, so different actions used to disagree on the boundary. This normalizes
 * once: snap the start RIGHT and the end LEFT to the nearest real word —
 * skipping whitespace and (unless `includeRemoved`) removed/cut elements.
 * Consumed by `resolveTranscriptSelection`; every selection action should go
 * through that single path rather than re-deriving boundaries.
 */
export interface NormalizedWordBoundaries {
  startIndex: number;
  endIndex: number;
}

export function normalizeWordBoundaries(
  rawStartIndex: number,
  rawEndIndex: number,
  elements: TranscriptElement[],
  opts?: { includeRemoved?: boolean },
): NormalizedWordBoundaries | null {
  const includeRemoved = opts?.includeRemoved ?? false;
  const isRealWord = (el: TranscriptElement) =>
    el?.type === 'text' &&
    (el as { subtype?: string }).subtype !== 'whitespace' &&
    (includeRemoved || (el.state !== 'removed' && el.state !== 'cut'));

  const startIndex = getClosestElementIndexToRightByFilter(
    rawStartIndex,
    elements,
    isRealWord,
  );
  const endIndex = getClosestElementIndexToLeftByFilter(
    rawEndIndex,
    elements,
    isRealWord,
  );
  if (startIndex === -1 || endIndex === -1 || endIndex < startIndex) {
    return null;
  }
  return { startIndex, endIndex };
}

/**
 * The canonical resolved selection every transcript action consumes — produced
 * by `resolveTranscriptSelection` in `useTranscriptOperations`. Actions map
 * this to their own needs (time range, segment ids, text) instead of computing
 * boundaries themselves.
 */
export interface ResolvedTranscriptSelection {
  startIndex: number;
  endIndex: number;
  startTime: number;
  endTime: number;
  text: string;
}

export function getClosestElementIndexToRightByFilter(
  startIdx: number,
  elements: TranscriptElement[],
  filter: (el: TranscriptElement, index: number) => boolean,
) {
  let textIndexToRight = -1;
  if (startIdx < 0) startIdx = 0;
  while (startIdx < elements.length) {
    const el = elements[startIdx];
    if (el !== undefined && filter(el, startIdx)) {
      textIndexToRight = startIdx;
      break;
    }
    startIdx++;
  }

  return textIndexToRight;
}

export function getClosestNotRemovedNotWhiteSpaceElementIndexToLeft(
  startIdx: number,
  elements: TranscriptElement[],
) {
  let textIndexToLeft = -1;
  while (startIdx >= 0) {
    if (
      elements[startIdx].state !== 'removed' &&
      elements[startIdx].state !== 'cut' &&
      elements[startIdx].value !== ' ' &&
      elements[startIdx].value !== ''
    ) {
      textIndexToLeft = startIdx;
      break;
    }
    startIdx--;
  }

  return textIndexToLeft;
}

export function getClosestNotRemovedNotWhiteSpaceElementToRight(
  startIdx: number,
  elements: TranscriptElement[],
) {
  let textIndexToLeft = -1;
  while (startIdx < elements.length) {
    if (
      elements[startIdx].state !== 'removed' &&
      elements[startIdx].state !== 'cut' &&
      elements[startIdx].value !== ' ' &&
      elements[startIdx].value !== ''
    ) {
      textIndexToLeft = startIdx;
      break;
    }
    startIdx++;
  }

  return textIndexToLeft;
}

export function getClosestNotRemovedTextIndexToLeft(
  startIdx: number,
  elements: TranscriptElement[],
) {
  let textIndexToLeft = -1;
  if (startIdx > elements.length) {
    startIdx = elements.length - 1;
  }
  while (startIdx >= 0) {
    // console.log('check for text (left)', startIdx);
    if (
      elements[startIdx].state !== 'removed' &&
      elements[startIdx].state !== 'cut' &&
      elements[startIdx].type === 'text'
    ) {
      textIndexToLeft = startIdx;
      break;
    }
    startIdx--;
  }

  return textIndexToLeft;
}

/**
 * Story-absolute time window of a clip — the non-throwing, dependency-free
 * extraction of `AIClipProcessor.ensureClipData`'s derivation (which is
 * private, mutates the clip, and requires a `VideoEditor`, so UI surfaces
 * cannot call it). Primary path: the clip's stored `startTime`/`duration`.
 * Fallback: derive from `transcriptPosition` over the provided story
 * transcript elements, skipping removed/cut words. Returns `undefined` when
 * underivable — callers degrade gracefully (CLAUDE.md: warn, never throw in
 * validators). (PLAT-10286)
 */
export function deriveClipTimeRange(
  clip:
    | {
        startTime?: number;
        duration?: number;
        transcriptPosition?: { startIndex: number; endIndex: number };
      }
    | null
    | undefined,
  storyElements?: TranscriptElement[],
): { start: number; end: number } | undefined {
  if (!clip) return undefined;

  let start = typeof clip.startTime === 'number' ? clip.startTime : undefined;
  let end =
    start !== undefined && typeof clip.duration === 'number'
      ? start + clip.duration
      : undefined;

  if (
    (start === undefined || end === undefined) &&
    clip.transcriptPosition &&
    storyElements?.length
  ) {
    if (start === undefined) {
      const i = getClosestNotRemovedTextIndexToRight(
        clip.transcriptPosition.startIndex,
        storyElements,
      );
      const el = i >= 0 ? storyElements[i] : undefined;
      if (el && 'ts' in el && el.ts != null) start = el.ts;
    }
    if (start !== undefined && end === undefined) {
      const i = getClosestNotRemovedTextIndexToLeft(
        clip.transcriptPosition.endIndex,
        storyElements,
      );
      const el = i >= 0 ? storyElements[i] : undefined;
      if (el && 'end_ts' in el && el.end_ts != null) end = el.end_ts;
    }
  }

  if (start === undefined || end === undefined || end <= start) {
    return undefined;
  }
  return { start, end };
}

export function getClosestTextIndexToRight(
  startIdx: number,
  elements: TranscriptElement[],
  includeMuted: boolean = true,
) {
  let textIndexToRight = -1;
  while (startIdx < elements.length) {
    // console.log('check for text', startIdx);
    if (
      elements[startIdx].type === 'text' &&
      (includeMuted || elements[startIdx].state !== 'muted')
    ) {
      textIndexToRight = startIdx;
      break;
    }
    startIdx++;
  }

  return textIndexToRight;
}

export function getClosestTextIndexToLeft(
  startIdx: number,
  elements: TranscriptElement[],
) {
  let textIndexToLeft = -1;
  while (startIdx >= 0) {
    // console.log('check for text (left)', startIdx);
    if (elements[startIdx].type === 'text') {
      textIndexToLeft = startIdx;
      break;
    }
    startIdx--;
  }

  return textIndexToLeft;
}

export function getClosestRemovedIndexToLeft(
  startIdx: number,
  elements: TranscriptElement[],
) {
  let textIndexToLeft = -1;
  while (startIdx >= 0) {
    //todo better condition
    if (
      elements[startIdx].state === 'removed' ||
      elements[startIdx].state === 'cut' ||
      elements[startIdx].state === 'muted' ||
      !elements[startIdx].value
    ) {
      textIndexToLeft = startIdx;
      break;
    }
    startIdx--;
  }

  return textIndexToLeft;
}

export function getClosestRemovedIndexToRight(
  startIdx: number,
  elements: TranscriptElement[],
) {
  let textIndexToLeft = -1;
  while (startIdx < elements.length) {
    //todo better condition
    if (
      elements[startIdx].state === 'removed' ||
      elements[startIdx].state === 'cut' ||
      elements[startIdx].state === 'muted' ||
      !elements[startIdx].value
    ) {
      textIndexToLeft = startIdx;
      break;
    }
    startIdx++;
  }

  return textIndexToLeft;
}

export function getCutTimeBetweenTwoWords(
  firstElement?: TranscriptElement,
  nextElement?: TranscriptElement,
): number {
  if (firstElement && firstElement.type !== 'text')
    throw Error(`Not a word element ${firstElement}`);
  if (nextElement && nextElement.type !== 'text')
    throw Error(`Not a word element ${firstElement}`);
  if (!firstElement && !nextElement)
    throw Error(`At least one element required to calculate cut time`);
  if (!nextElement) return firstElement!.end_ts!;
  if (!firstElement) {
    return Math.max(0, nextElement.ts! - FIRST_WORD_CUT_BUFFER_SECONDS);
  }

  return firstElement.end_ts + (firstElement.buffer_after_ts || 0);
}

// TODO: Move to TranscriptManager
export function applyDeletionToTranscript(
  fromTs: number,
  toTs: number,
  inPlace: boolean,
  elements: TranscriptElement[],
): TranscriptChange | null {
  // if (fromTs > toTs) throw Error('toTs must be greater than fromTs');
  let fromIndex, toIndex;
  const fromTsAdjusted = fromTs - 1e-6; // Adjust to avoid floating point issues
  // find word th
  const fromWordIndex = elements.findIndex(
    el =>
      el.state !== 'removed' &&
      el.state !== 'cut' &&
      el.end_ts &&
      (el.ts >= fromTsAdjusted || el.end_ts > fromTsAdjusted) &&
      el.ts < toTs,
  );
  const toWordIndex = elements.findIndex(
    el =>
      el.state !== 'removed' &&
      el.state !== 'cut' &&
      el.end_ts &&
      el.end_ts > toTs,
  );

  // todo: if fromWordIndex === toWordIndex
  if (fromWordIndex === -1) return null;

  // simple case, word is fully in the [fromTsAdjusted; toTs] interval
  if (elements[fromWordIndex]?.ts! > fromTsAdjusted) {
    fromIndex = fromWordIndex;
  } else {
    // todo choose between current and prev word
    const isCloserToEnd =
      fromTs - elements[fromWordIndex]?.ts! >
      elements[fromWordIndex]?.end_ts! - fromTs;
    fromIndex = isCloserToEnd
      ? getClosestNotRemovedTextIndexToRight(fromWordIndex + 1, elements)
      : fromWordIndex;
  }

  if (fromIndex === -1) return null;

  if (toWordIndex === -1) {
    toIndex = elements.length - 1; // till last word
  } else if (elements[toWordIndex]?.ts! > toTs) {
    toIndex = getClosestNotRemovedTextIndexToLeft(toWordIndex - 1, elements);
  } else {
    const isCloserToEnd =
      toTs - elements[toWordIndex]?.ts! > elements[toWordIndex]?.end_ts! - toTs;
    toIndex = isCloserToEnd
      ? toWordIndex
      : getClosestNotRemovedTextIndexToLeft(toWordIndex - 1, elements);
  }

  if (toIndex < fromIndex) {
    toIndex = fromIndex;
  }

  const timeBufferBefore = +(elements[fromIndex]?.ts! - fromTs).toFixed(3);
  let timeBufferAfter = +(toTs - elements[toIndex]?.end_ts!).toFixed(3);
  if (fromIndex === toIndex + 1) {
    timeBufferAfter = +(toTs - elements[fromIndex]?.ts!).toFixed(3);
  }

  return {
    version: 2,
    type: 'remove',
    index: fromIndex,
    count: toIndex - fromIndex + 1,
    oldValue: elements
      .slice(fromIndex, toIndex + 1)
      .map(el => el.value || '')
      .join(''),
    newValue: null,
    timeBufferBefore,
    timeBufferAfter,
    inPlace,
    datetime: new Date().toISOString(),
  };
}

// TODO: Move to TranscriptManager
export function applyShiftingToTranscript(
  fromTs: number,
  toTs: number,
  intoTs: number,
  elements: TranscriptElement[],
): TranscriptChange | null {
  const filterRemoved = (el: TranscriptElement) =>
    el.state !== 'removed' && el.state !== 'cut';

  let fromIndex, toIndex, intoIndex;
  const fromWordIndex = elements.findIndex(
    el => filterRemoved(el) && el.end_ts && el.end_ts > fromTs,
  );
  const toWordIndex = elements.findIndex(
    el => filterRemoved(el) && el.end_ts && el.end_ts > toTs,
  );
  const afterWordIndex = elements.findIndex(
    el => filterRemoved(el) && el.end_ts && el.end_ts > intoTs,
  );

  if (fromWordIndex === -1) {
    return null;
    // todo empty shift
  }

  // simple case, word is fully in the [fromTs; toTs] interval
  if (elements[fromWordIndex]?.ts! >= fromTs) {
    fromIndex = fromWordIndex;
  } else {
    // todo choose between current and prev word
    const isCloserToEnd =
      fromTs - elements[fromWordIndex]?.ts! >
      elements[fromWordIndex]?.end_ts! - fromTs;
    fromIndex = isCloserToEnd
      ? getClosestNotRemovedTextIndexToRight(fromWordIndex + 1, elements)
      : fromWordIndex;
  }

  if (fromIndex === -1) return null;

  if (toWordIndex === -1) {
    toIndex = getClosestNotRemovedElementIndexToLeft(
      elements.length - 1,
      elements,
    ); // till last word
  } else if (elements[toWordIndex]?.ts! >= toTs) {
    toIndex = getClosestNotRemovedElementIndexToLeft(toWordIndex - 1, elements);
  } else {
    const isCloserToEnd =
      toTs - elements[toWordIndex]?.ts! > elements[toWordIndex]?.end_ts! - toTs;
    toIndex = isCloserToEnd
      ? toWordIndex
      : getClosestNotRemovedElementIndexToLeft(toWordIndex - 1, elements);
  }
  const closestBreakOrTextIndex = getClosestElementIndexToRightByFilter(
    Math.min(toIndex + 1, elements.length - 1),
    elements,
    el => filterRemoved(el) && (el.karaoke_break || el.type === 'text'),
  );
  toIndex = elements[closestBreakOrTextIndex]?.karaoke_break
    ? closestBreakOrTextIndex
    : toIndex;

  if (afterWordIndex === -1) {
    // into the end
    intoIndex = elements.length - 1;
  } else if (afterWordIndex >= fromIndex && afterWordIndex <= toIndex) {
    // todo check
    intoIndex = fromIndex;
  } else if (elements[afterWordIndex]?.ts! > intoTs) {
    intoIndex = afterWordIndex;
  } else {
    const isCloserToEnd =
      intoTs - elements[afterWordIndex]?.ts! >
      elements[afterWordIndex]?.end_ts! - intoTs;
    intoIndex = isCloserToEnd
      ? getClosestNotRemovedTextIndexToRight(afterWordIndex + 1, elements)
      : afterWordIndex;
  }

  return {
    type: 'shift',
    index: fromIndex,
    count: toIndex - fromIndex + 1,
    newIndex: intoIndex,
    timeShift: intoTs - fromTs,
    datetime: new Date().toISOString(),
  };
}

export const getRoundedTo = (time: number, precision: number) => {
  return Math.round(time / precision) / (1 / precision);
};

export function test_injectTranscriptionElementsInTimeline(
  transcriptionElements: TranscriptElement[],
) {
  const elements = [];
  // check if top track is free
  let track = KARAOKE_TRACK_NUMBER; //fix karaoke on top track
  for (let i = 0; i < transcriptionElements.length; i++) {
    const element = transcriptionElements[i];
    if (
      element.type !== 'text' ||
      element.state === 'removed' ||
      element.state === 'cut'
    )
      continue;
    const duration =
      element.end_ts - element.ts > 0 ? element.end_ts - element.ts : 0; //MIN DURATION
    elements.push({
      id: 'transcription-elemenet-' + i,
      track: track,
      globalTime: element.ts,
      localTime: element.ts,
      time: element.ts,
      duration,
      exitDuration: 0,
      type: 'text',
      text: element.value,
      source: {
        type: 'text',
        track: track,
        text: element.value,
      },
    });
  }
  return elements as unknown as ElementState[];
}

export const convertToPixels = (
  value: number,
  units: string,
  dimensions: { width: number; height: number },
) => {
  if (units === 'vh') {
    return (dimensions.height * value) / 100;
  }
  if (units === 'vw') {
    return (dimensions.width * value) / 100;
  }
  return value;
};

export const convertFromPixels = (
  value: number,
  toUnits: string,
  dimensions: { width: number; height: number },
) => {
  if (toUnits === 'vh') {
    return (100 * value) / dimensions.height;
  }
  if (toUnits === 'vw') {
    return (100 * value) / dimensions.width;
  }
  return value;
};

const FIRST_WORD_CUT_BUFFER_SECONDS = 0.2;

export const getMinMaxYPosition = (
  sourceHeight: number | undefined,
  selectedFontSize: string,
  lines?: 1 | 2 | 3 | 4 | 5 | 6 | 7,
  karaokeElement?: KaraokeConfig,
) => {
  let min = 0;
  let max = 100;
  let height = sourceHeight || 720;
  const fontSizeUnits = selectedFontSize.match(/[a-z]+/)?.[0] || 'px';
  const fontSize = parseFloat(selectedFontSize);
  if (karaokeElement?.instagramEffect && lines) {
    const smallFontSize = 1.1 * 0.8 * fontSize; // 1.25 - line height multiplier, 0.8 - multiplier for small line
    const largeFontSize = 1.1 * 1.25 * fontSize; // 1.25 - line height multiplier, 1.25 - multiplier for large line
    min =
      fontSizeUnits === 'px'
        ? convertFromPixels(largeFontSize / 2, 'vh', { height, width: 0 })
        : largeFontSize / 2;
    const largeLines = Math.floor((2 * lines) / 5);
    const smallLines = lines - largeLines;
    max =
      102 -
      (fontSizeUnits === 'px'
        ? convertFromPixels(
            largeLines * largeFontSize + smallLines * smallFontSize,
            'vh',
            { height, width: 0 },
          )
        : largeLines * largeFontSize + smallLines * smallFontSize);
  } else {
    min =
      fontSizeUnits === 'px'
        ? convertFromPixels(fontSize, 'vh', { height, width: 0 })
        : fontSize;
    max =
      100 -
      (fontSizeUnits === 'px'
        ? convertFromPixels(2 * fontSize, 'vh', { height, width: 0 })
        : 2 * fontSize);
  }
  return [min, max];
};

export function isSentenceEnd(value: string | null): boolean {
  // eslint-disable-next-line no-useless-escape
  return !!value?.match(/[\.\!\?]\s*$/);
}

export function capitalize(value: string) {
  return value.charAt(0).toLocaleUpperCase() + value.slice(1);
}

export function decapitalize(value: string) {
  return value.charAt(0).toLocaleLowerCase() + value.slice(1);
}

export function mapToSource(state: Record<string, any>): Record<string, any> {
  if (!state) {
    return {};
  } else if (state.elements) {
    return {
      ...state.source,
      elements: state.elements.map((element: any) => mapToSource(element)),
    };
  } else {
    return state.source;
  }
}

export function adjustTrackNumbersToStartFromOne(source: any) {
  const newSource = deepClone(source);
  const minTrackNumber = Math.min(
    ...newSource.elements.map((el: any) => el.track),
  );
  if (minTrackNumber > 1) {
    newSource.elements.forEach((el: any) => {
      if (el.track !== KARAOKE_TRACK_NUMBER) {
        el.track -= minTrackNumber - 1;
      }
    });
  }
  return newSource;
}
